import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, ExternalLink, Building2, Globe, MapPin, Users, Calendar, Briefcase, Tag, Flame } from "lucide-react";
import { getCompanyDetail, getCompanyJobPostings, type CompanyDetail, type CompanyJobPostingsResponse } from "@/services/companiesApi";
import { AppSidebar } from "@/components/sidebar/app-sidebar";
import { SidebarInset, SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";
import { Separator } from "@/components/ui/separator";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { companySourceLabel, companyTypeLabel } from "@/app/company-classification/classification";

const API_URL = import.meta.env.VITE_API_URL;

function formatLocation(loc: { line1?: string | null; line2?: string | null; city?: string | null; postal_code?: string | null; geographic_area?: string | null; country?: string | null }) {
  return [loc.line1, loc.line2, [loc.postal_code, loc.city].filter(Boolean).join(" "), loc.geographic_area, loc.country].filter(Boolean).join(", ");
}

export default function CompanyDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [company, setCompany] = useState<CompanyDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [jobs, setJobs] = useState<CompanyJobPostingsResponse | null>(null);
  const [jobsLoading, setJobsLoading] = useState(true);

  useEffect(() => {
    if (!id) return;
    getCompanyDetail(navigate, Number(id))
      .then(setCompany)
      .catch(() => navigate("/companies"))
      .finally(() => setLoading(false));
  }, [id, navigate]);

  useEffect(() => {
    if (!id) return;
    // active_only=false : on montre aussi les offres fermées (badge), mais
    // active_count / mass_hiring restent calculés sur les actives côté serveur.
    getCompanyJobPostings(navigate, Number(id), { active_only: false, size: 100 })
      .then(setJobs)
      .catch(() => setJobs(null))
      .finally(() => setJobsLoading(false));
  }, [id, navigate]);

  if (loading) return <SidebarProvider><AppSidebar /><SidebarInset><div className="p-8 text-center text-muted-foreground">Chargement...</div></SidebarInset></SidebarProvider>;
  if (!company) return null;

  const staffRange = company.staff_count_range_start
    ? `${company.staff_count_range_start}-${company.staff_count_range_end ?? "?"}`
    : null;

  return (
    <SidebarProvider>
      <AppSidebar />
      <SidebarInset>
        <header className="flex h-16 shrink-0 items-center gap-2 transition-[width,height] ease-linear group-has-[[data-collapsible=icon]]/sidebar-wrapper:h-12">
          <div className="flex items-center gap-2 px-4">
            <SidebarTrigger className="-ml-1" />
            <Separator orientation="vertical" className="mr-2 h-4" />
            <h1 className="text-sm font-medium">Entreprise : {company.name}</h1>
          </div>
        </header>

    <div className="p-6 max-w-3xl mx-auto space-y-6">
      <Button variant="ghost" size="sm" onClick={() => navigate("/companies")}>
        <ArrowLeft className="h-4 w-4 mr-2" /> Retour
      </Button>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-3">
            <img
              src={`${API_URL}/companies/${company.id}/image`}
              alt=""
              className="h-12 w-12 rounded-lg bg-muted object-cover"
              onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }}
            />
            <div>
              <div className="flex items-center gap-2">
                <Building2 className="h-5 w-5" />
                {company.name}
              </div>
              {company.industry && (
                <Badge variant="secondary" className="mt-1">{company.industry}</Badge>
              )}
              {company.company_type && (
                <Badge variant="outline" className="mt-1 ml-1">
                  {companyTypeLabel(company.company_type)}
                  {company.company_type_source ? ` · ${companySourceLabel(company.company_type_source)}` : ""}
                </Badge>
              )}
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Links */}
          <div className="flex flex-wrap gap-3">
            {company.profile_url && (
              <a href={company.profile_url} target="_blank" rel="noopener noreferrer"
                 className="text-sm text-blue-600 hover:underline flex items-center gap-1">
                <ExternalLink className="h-3.5 w-3.5" /> LinkedIn
              </a>
            )}
            {company.website_url && (
              <a href={company.website_url} target="_blank" rel="noopener noreferrer"
                 className="text-sm text-blue-600 hover:underline flex items-center gap-1">
                <Globe className="h-3.5 w-3.5" /> Site web
              </a>
            )}
          </div>

          {/* Details grid */}
          <dl className="grid grid-cols-2 gap-4 text-sm">
            {company.headquarters && (
              <div>
                <dt className="text-muted-foreground flex items-center gap-1"><MapPin className="h-3.5 w-3.5" /> Siege</dt>
                <dd className="font-medium">{company.headquarters}</dd>
              </div>
            )}
            {(company.staff_count != null || company.employee_count_range) && (
              <div>
                <dt className="text-muted-foreground flex items-center gap-1"><Users className="h-3.5 w-3.5" /> Effectifs</dt>
                <dd className="font-medium">
                  {company.staff_count != null ? company.staff_count.toLocaleString("fr-FR") : company.employee_count_range}
                  {staffRange ? ` (fourchette: ${staffRange})` : ""}
                </dd>
              </div>
            )}
            {company.founded_year && (
              <div>
                <dt className="text-muted-foreground flex items-center gap-1"><Calendar className="h-3.5 w-3.5" /> Fondee</dt>
                <dd className="font-medium">{company.founded_year}</dd>
              </div>
            )}
            {company.follower_count != null && (
              <div>
                <dt className="text-muted-foreground">Abonnes LinkedIn</dt>
                <dd className="font-medium">{company.follower_count.toLocaleString("fr-FR")}</dd>
              </div>
            )}
            <div>
              <dt className="text-muted-foreground">ID</dt>
              <dd className="font-medium">{company.id}</dd>
            </div>
            <div>
              <dt className="text-muted-foreground">Ref. externe</dt>
              <dd className="font-medium font-mono text-xs">{company.external_ref}</dd>
            </div>
            {company.created && (
              <div>
                <dt className="text-muted-foreground">Cree le</dt>
                <dd className="font-medium">{new Date(company.created).toLocaleDateString("fr-FR")}</dd>
              </div>
            )}
            {company.updated && (
              <div>
                <dt className="text-muted-foreground">Mis a jour</dt>
                <dd className="font-medium">{new Date(company.updated).toLocaleDateString("fr-FR")}</dd>
              </div>
            )}
          </dl>

          {/* Industries */}
          {company.industries && company.industries.length > 0 && (
            <div>
              <p className="text-sm text-muted-foreground mb-2 flex items-center gap-1"><Briefcase className="h-3.5 w-3.5" /> Industries</p>
              <div className="flex flex-wrap gap-2">
                {company.industries.map((ind) => (
                  <span key={ind.id} className="px-2 py-1 bg-blue-50 text-blue-700 rounded-md text-xs font-medium">{ind.name}</span>
                ))}
              </div>
            </div>
          )}

          {/* Specialties */}
          {company.specialties && company.specialties.length > 0 && (
            <div>
              <p className="text-sm text-muted-foreground mb-2 flex items-center gap-1"><Tag className="h-3.5 w-3.5" /> Specialites</p>
              <div className="flex flex-wrap gap-1">
                {company.specialties.map((s, i) => (
                  <Badge key={i} variant="outline">{s}</Badge>
                ))}
              </div>
            </div>
          )}

          {/* Locations */}
          {company.locations && company.locations.length > 0 && (
            <div>
              <p className="text-sm text-muted-foreground mb-2 flex items-center gap-1"><MapPin className="h-3.5 w-3.5" /> Adresses</p>
              <ul className="space-y-2">
                {company.locations.map((loc, i) => (
                  <li key={i} className="text-sm">
                    {formatLocation(loc)}
                    {loc.is_headquarters && <span className="ml-2 text-xs text-green-700 bg-green-50 px-1.5 py-0.5 rounded">Siege</span>}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Description */}
          {company.description && (
            <div>
              <p className="text-sm text-muted-foreground mb-2">Description</p>
              <p className="text-sm whitespace-pre-line">{company.description}</p>
            </div>
          )}

          {/* Funding */}
          {(company.num_funding_rounds || (company.funding_rounds && company.funding_rounds.length > 0)) && (
            <div>
              <p className="text-sm text-muted-foreground mb-2 font-medium">Financement</p>
              {company.num_funding_rounds && (
                <p className="text-sm mb-2">{company.num_funding_rounds} tour(s) de financement
                  {company.crunchbase_url && (
                    <a href={company.crunchbase_url} target="_blank" rel="noopener noreferrer" className="ml-2 text-blue-600 hover:underline text-xs">Crunchbase</a>
                  )}
                </p>
              )}
              {company.funding_rounds?.map((fr) => (
                <div key={fr.id} className="border rounded p-3 mb-2 text-sm">
                  <div className="flex items-center gap-2 flex-wrap">
                    {fr.funding_type && <span className="px-2 py-0.5 bg-green-50 text-green-700 rounded text-xs font-medium">{fr.funding_type.replace(/_/g, " ")}</span>}
                    {fr.money_raised_amount && (
                      <span className="font-medium">
                        {new Intl.NumberFormat("en-US", { style: "currency", currency: fr.money_raised_currency || "USD", maximumFractionDigits: 0 }).format(fr.money_raised_amount)}
                      </span>
                    )}
                    {fr.announced_on && <span className="text-muted-foreground">{fr.announced_on}</span>}
                  </div>
                  {fr.investors.length > 0 && (
                    <div className="mt-1 text-xs text-muted-foreground">
                      Investisseurs : {fr.investors.map((inv, i) => (
                        <span key={i}>
                          {inv.crunchbase_url ? <a href={inv.crunchbase_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">{inv.name}</a> : inv.name}
                          {i < fr.investors.length - 1 && ", "}
                        </span>
                      ))}
                      {fr.num_other_investors ? ` +${fr.num_other_investors} autre(s)` : ""}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Offres d'emploi scrapées (company_jobs) + signal mass-hiring */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Briefcase className="h-4 w-4" /> Offres d'emploi
            {jobs && (
              <Badge variant="secondary" className="ml-1">{jobs.active_count} active(s)</Badge>
            )}
            {jobs?.mass_hiring && (
              <Badge className="bg-orange-100 text-orange-700 hover:bg-orange-100 flex items-center gap-1">
                <Flame className="h-3.5 w-3.5" /> Mass hiring (≥ {jobs.mass_hiring_threshold})
              </Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {jobsLoading ? (
            <p className="text-sm text-muted-foreground">Chargement des offres...</p>
          ) : !jobs || jobs.job_postings.length === 0 ? (
            <p className="text-sm text-muted-foreground">Aucune offre scrapée pour cette entreprise.</p>
          ) : (
            <ul className="divide-y">
              {jobs.job_postings.map((jp) => (
                <li key={jp.id} className="py-2 flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-sm truncate">{jp.title}</span>
                      {!jp.is_active && <Badge variant="outline" className="text-xs">fermée</Badge>}
                    </div>
                    <div className="text-xs text-muted-foreground flex flex-wrap gap-x-3">
                      {jp.location && <span className="flex items-center gap-1"><MapPin className="h-3 w-3" />{jp.location}</span>}
                      {jp.posted_at && <span>Publiée le {new Date(jp.posted_at).toLocaleDateString("fr-FR")}</span>}
                      {jp.fetched_at && <span>Vue le {new Date(jp.fetched_at).toLocaleDateString("fr-FR")}</span>}
                    </div>
                  </div>
                  {jp.external_url && (
                    <a href={jp.external_url} target="_blank" rel="noopener noreferrer"
                       className="text-blue-600 hover:underline text-xs flex items-center gap-1 shrink-0">
                      <ExternalLink className="h-3.5 w-3.5" /> Voir
                    </a>
                  )}
                </li>
              ))}
            </ul>
          )}
          {jobs && jobs.total > jobs.job_postings.length && (
            <p className="text-xs text-muted-foreground mt-2">
              {jobs.job_postings.length} sur {jobs.total} offres affichées.
            </p>
          )}
        </CardContent>
      </Card>
    </div>
      </SidebarInset>
    </SidebarProvider>
  );
}
